import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import html2pdf from "html2pdf.js";
import { Eye, Download, Printer, Search, Loader2, AlertCircle } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { TERM_OPTIONS } from "../../constants/assessments";
import { useTenant } from "../../context/TenantContext";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";
import "../../styles/ReportCardsViewer.css";
import { useActiveLearner } from "../../hooks/useActiveLearner";
import { getStudentAuthHeaders } from "../../utils/studentSession";

interface ReportCard {
  id: number;
  studentId: number;
  studentName: string;
  admissionNumber?: string | null;
  classId: number;
  className: string;
  academicYear: number;
  term: string;
  overallGrade: string;
  overallScore: number | null;
  generatedAt: string;
  generatedByName: string;
  approvedByName?: string | null;
  approvedAt: string | null;
  details?: ReportCardDetail[];
  isPlaceholder?: boolean;
  attendanceSummary?: {
    totalDays: number;
    presentDays: number;
    absentDays: number;
  } | null;
  position?: number | null;
  classSize?: number | null;
  nextTermStart?: string | null;
  classTeacherRemark?: string | null;
  headteacherRemark?: string | null;
  status?: string | null;
  classTeacherComment?: string | null;
  classTeacherCommentByName?: string | null;
  classTeacherCommentAt?: string | null;
  headteacherComment?: string | null;
  headteacherCommentByName?: string | null;
  headteacherCommentAt?: string | null;
  age?: number | string | null;
  assessmentBreakdown?: Array<{
    subjectName: string;
    subjectShortName?: string | null;
    subjectLabel?: string | null;
    monthly: number | null;
    midterm: number | null;
    endterm: number | null;
    average: number | null;
    grade: string | null;
  }>;
}

interface ReportCardDetail {
  id: number;
  subjectId: number;
  subjectName: string;
  subjectShortName?: string | null;
  score: number | null;
  grade: string | null;
  remark: string | null;
  teacherRemark: string | null;
}

interface ClassOption {
  id: number;
  name: string;
}

interface StudentOption {
  id: number;
  name: string;
}

interface SchoolHeaderInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
}

const getCurrentAcademicTerm = () => {
  const month = new Date().getMonth() + 1;
  if (month >= 9) return "Term Three";
  if (month >= 5) return "Term Two";
  return "Term One";
};

const formatAgeDisplay = (age?: number | string | null) => {
  if (age === null || age === undefined) return "Not recorded";
  if (typeof age === "number") {
    return Number.isFinite(age) ? String(age) : "Not recorded";
  }
  const normalized = String(age).trim();
  return normalized ? normalized : "Not recorded";
};

const SAVE_PROGRESS_INTERVAL = 10;

const ReportCardsViewer: React.FC = () => {
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [selectedStudent, setSelectedStudent] = useState<string>("");
  const [selectedTerm, setSelectedTerm] = useState<string>(getCurrentAcademicTerm());
  const [selectedYear, setSelectedYear] = useState<string>(String(new Date().getFullYear()));
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [reportCards, setReportCards] = useState<ReportCard[]>([]);
  const [reportCardDetailsById, setReportCardDetailsById] = useState<Record<number, ReportCard>>({});
  const [selectedReportCard, setSelectedReportCard] = useState<ReportCard | null>(null);
  const [editingAllComments, setEditingAllComments] = useState(false);
  const [commentDraftsById, setCommentDraftsById] = useState<Record<number, { teacher: string; headteacher: string }>>({});
  const [previewReportId, setPreviewReportId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [resultAvailability, setResultAvailability] = useState<{
    rows: number;
    learners: number;
  }>({ rows: 0, learners: 0 });
  const [schoolDetails, setSchoolDetails] = useState<SchoolHeaderInfo>({
    name: "",
    address: "",
    phone: "",
    email: ""
  });

  const token = localStorage.getItem("token");
  const userRole = (localStorage.getItem("role") || "").toLowerCase();
  const { activeLearner, activeLearnerId } = useActiveLearner();
  const canGenerate = userRole === "admin" || userRole === "headteacher" || userRole === "superadmin";
  const canEditTeacherComment = userRole === "teacher" || userRole === "headteacher" || userRole === "admin" || userRole === "superadmin";
  const canEditHeadteacherComment = userRole === "headteacher" || userRole === "admin" || userRole === "superadmin";
  const { branding, tenant } = useTenant();
  const schoolName = schoolDetails.name || branding.displayName;
  const schoolLogo = branding.logoUrl || "";
  const brandPrimary = tenant?.primary_color || branding.primaryColor;
  const brandSecondary = tenant?.secondary_color || branding.secondaryColor;

  const terms = TERM_OPTIONS;
  const yearOptions = Array.from({ length: 6 }, (_, idx) => String(new Date().getFullYear() - idx));

  const fetchClasses = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl("academics/classes"), {
        headers: getStudentAuthHeaders(token)
      });
      const classList = res.data?.classes || [];
      setClasses(classList);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  }, [token]);

  const fetchSchoolDetails = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(getApiUrl("school/details"), {
        headers: getStudentAuthHeaders(token)
      });
      setSchoolDetails({
        name: res.data?.name || "",
        address: res.data?.address || "",
        phone: res.data?.phone || "",
        email: res.data?.email || ""
      });
    } catch (error) {
      console.error("Error fetching school details:", error);
    }
  }, [token]);

  const fetchStudents = useCallback(async (classId: string) => {
    if (!classId) {
      setStudents([]);
      return;
    }
    try {
      const res = await axios.get(getApiUrl(`academics/classes/${classId}/students`), {
        headers: getStudentAuthHeaders(token)
      });
      setStudents(res.data?.students || res.data || []);
    } catch (error) {
      console.error("Error fetching students:", error);
      setStudents([]);
    }
  }, [token]);

  const handleClassChange = (classId: string) => {
    setSelectedClass(classId);
    setSelectedStudent(""); // Reset student selection when class changes
    fetchStudents(classId);
  };

  const fetchReportCards = useCallback(async () => {
    setLoading(true);
    setFiltersApplied(true);
    try {
      const params: Record<string, string> = {};
      if (selectedClass) params.classId = selectedClass;
      if (selectedStudent) params.studentId = selectedStudent;
      if (selectedTerm) params.term = selectedTerm;
      if (selectedYear) params.academicYear = selectedYear;

      let cards: ReportCard[] = [];
      if (userRole === "student" && activeLearnerId) {
        const res = await axios.get(getApiUrl(`report-cards/student/${activeLearnerId}`), {
          headers: getStudentAuthHeaders(token)
        });
        cards = (res.data || []).map((card: any) => ({
          ...card,
          studentName: activeLearner?.name || card.studentName,
          academicYear: card.academicYear ?? card.year,
          overallScore: card.overallScore ?? null,
          className: card.className || activeLearner?.className || "Unassigned"
        }));
      } else if (selectedClass) {
        const res = await axios.get(getApiUrl(`report-cards/class/${selectedClass}`), {
          headers: getStudentAuthHeaders(token),
          params
        });
        // For other roles, transform the data to match our interface
        cards = (res.data || []).map((card: any) => ({
          ...card,
          academicYear: card.academicYear ?? card.year,
          term: card.term,
          overallGrade: card.overallGrade,
          overallScore: card.overallScore ?? null,
          generatedByName: card.generatedByName,
          approvedAt: card.approvedAt
        }));
      } else if (userRole === "admin" || userRole === "superadmin") {
        const res = await axios.get(getApiUrl("report-cards/all"), {
          headers: getStudentAuthHeaders(token),
          params
        });
        cards = (res.data || []).map((card: any) => ({
          ...card,
          academicYear: card.academicYear ?? card.year,
          term: card.term,
          overallGrade: card.overallGrade,
          overallScore: card.overallScore ?? null,
          generatedByName: card.generatedByName,
          approvedAt: card.approvedAt
        }));
      } else if (!selectedClass && userRole !== "student") {
        setReportCards([]);
        setMessage("Select a class to view report cards.");
        return;
      }

      let merged = cards;
      if (selectedClass && userRole !== "student") {
        const classNameMap = new Map(classes.map((cls) => [String(cls.id), cls.name]));
        const selectedClassName = selectedClass ? (classNameMap.get(selectedClass) || "Unassigned") : "Unassigned";
        const cardsByStudent = new Map<number, ReportCard>();
        cards.forEach((card) => cardsByStudent.set(card.studentId, card));
        const fallbackYear = Number(selectedYear) || new Date().getFullYear();

        merged = students.map((student) => {
          const existing = cardsByStudent.get(student.id);
          if (existing) return existing;
          return {
            id: -student.id,
            studentId: student.id,
            studentName: student.name,
            classId: selectedClass ? Number(selectedClass) : 0,
            className: selectedClassName,
            academicYear: fallbackYear,
            term: selectedTerm || "All",
            overallGrade: "-",
            overallScore: null,
            generatedAt: "",
            generatedByName: "",
            approvedAt: null,
            isPlaceholder: true
          };
        });

        const missingCards = cards.filter((card) => !cardsByStudent.has(card.studentId));
        merged = [...merged, ...missingCards];
      }

      if (selectedStudent) {
        const selectedId = Number(selectedStudent);
        merged = merged.filter((card) => card.studentId === selectedId);
      }

// Filter by search query if provided
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        merged = merged.filter((card: ReportCard) =>
          card.studentName.toLowerCase().includes(query) ||
          String(card.studentId).includes(query) ||
          String(card.admissionNumber || "").toLowerCase().includes(query)
        );
      }

      setReportCards(merged);
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Error fetching report cards");
    } finally {
      setLoading(false);
    }
  }, [token, selectedClass, selectedStudent, selectedTerm, selectedYear, searchQuery, userRole, classes, students, activeLearnerId, activeLearner]);

  const fetchResultAvailability = useCallback(async () => {
    if (!token || !selectedClass || !selectedTerm || !selectedYear) {
      setResultAvailability({ rows: 0, learners: 0 });
      return;
    }

    try {
      const res = await axios.get(
        getApiUrl(`assessments/results/class/${selectedClass}`),
        {
          headers: getStudentAuthHeaders(token),
          params: {
            term: selectedTerm,
            year: selectedYear
          }
        }
      );

      const rows = Array.isArray(res.data) ? res.data : [];
      const learnerIds = new Set(
        rows
          .map((row: any) => row.studentId)
          .filter((value: any) => value !== null && value !== undefined)
      );

      setResultAvailability({
        rows: rows.length,
        learners: learnerIds.size
      });
    } catch (error) {
      console.error("Error checking assessment result availability:", error);
      setResultAvailability({ rows: 0, learners: 0 });
    }
  }, [selectedClass, selectedTerm, selectedYear, token]);

  const loadReportCardDetails = useCallback(async (reportCardId: number) => {
    const res = await axios.get(getApiUrl(`report-cards/${reportCardId}`), {
      headers: getStudentAuthHeaders(token)
    });
    const details = res.data || null;
    return details
      ? {
          ...details,
          academicYear: details.academicYear ?? details.year
        }
      : null;
  }, [token]);

  const fetchReportCardDetails = async (reportCardId: number) => {
    setViewLoading(true);
    try {
      const details = await loadReportCardDetails(reportCardId);
      if (details) {
        setReportCardDetailsById((current) => ({ ...current, [reportCardId]: details }));
        setSelectedReportCard(details);
        setCommentDraftsById((current) => ({
          ...current,
          [reportCardId]: {
            teacher: details.classTeacherComment || "",
            headteacher: details.headteacherComment || ""
          }
        }));
      } else {
        setSelectedReportCard(null);
      }
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Error fetching report card details");
    } finally {
      setViewLoading(false);
    }
  };

  useEffect(() => {
    if (userRole !== "student") {
      fetchClasses();
    }
    fetchSchoolDetails();
    // Reset filtersApplied on initial load
    setFiltersApplied(false);
  }, [fetchClasses, fetchSchoolDetails, userRole]);

  useEffect(() => {
    fetchStudents(selectedClass);
  }, [selectedClass, fetchStudents]);

  useEffect(() => {
    fetchReportCards();
  }, [fetchReportCards]);

  useEffect(() => {
    fetchResultAvailability();
  }, [fetchResultAvailability]);

  useEffect(() => {
    if (!message) return;
    const timeoutId = window.setTimeout(() => setMessage(null), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [message]);

  useEffect(() => {
    const cardsToHydrate = reportCards.filter((card) => !card.isPlaceholder && !reportCardDetailsById[card.id]);
    if (cardsToHydrate.length === 0) return;

    let cancelled = false;

    const hydrateCards = async () => {
      try {
        const detailedCards = await Promise.all(
          cardsToHydrate.map(async (card) => {
            const details = await loadReportCardDetails(card.id);
            return details ? [card.id, details] as const : null;
          })
        );

        if (cancelled) return;

        setReportCardDetailsById((current) => {
          const next = { ...current };
          detailedCards.forEach((entry) => {
            if (entry) {
              next[entry[0]] = entry[1];
            }
          });
          return next;
        });
      } catch (error) {
        console.error("Error hydrating report card details:", error);
      }
    };

    hydrateCards();

    return () => {
      cancelled = true;
    };
  }, [reportCards, reportCardDetailsById, loadReportCardDetails]);

  useEffect(() => {
    const available = reportCards.filter((card) => !card.isPlaceholder);
    if (available.length === 0) {
      setPreviewReportId(null);
      return;
    }
    if (selectedStudent) {
      const match = available.find((card) => String(card.studentId) === String(selectedStudent));
      if (match) {
        setPreviewReportId(match.id);
        fetchReportCardDetails(match.id);
      } else {
        setPreviewReportId(null);
      }
      return;
    }
    const resolvedId = previewReportId && available.some((card) => card.id === previewReportId)
      ? previewReportId
      : available[0].id;
    setPreviewReportId(resolvedId);
    fetchReportCardDetails(resolvedId);
  }, [reportCards, selectedStudent]);

  const handleViewReportCard = (reportCard: ReportCard) => {
    if (reportCard.details) {
      setSelectedReportCard(reportCard);
      setCommentDraftsById((current) => ({
        ...current,
        [reportCard.id]: {
          teacher: reportCard.classTeacherComment || "",
          headteacher: reportCard.headteacherComment || ""
        }
      }));
    } else {
      fetchReportCardDetails(reportCard.id);
    }
  };

  const startEditingComments = () => {
    const nextDrafts: Record<number, { teacher: string; headteacher: string }> = {};
    reportCards
      .filter((card) => !card.isPlaceholder)
      .forEach((card) => {
        const enrichedCard =
          selectedReportCard?.id === card.id
            ? selectedReportCard
            : reportCardDetailsById[card.id] || card;
        nextDrafts[card.id] = {
          teacher: enrichedCard.classTeacherComment || "",
          headteacher: enrichedCard.headteacherComment || ""
        };
      });
    setCommentDraftsById(nextDrafts);
    setEditingAllComments(true);
  };

  const cancelEditingComments = () => {
    setEditingAllComments(false);
    setCommentDraftsById({});
  };

  const getCommentDraftsForCard = (reportCard: ReportCard) => {
    return commentDraftsById[reportCard.id] || {
      teacher: reportCard.classTeacherComment || "",
      headteacher: reportCard.headteacherComment || ""
    };
  };

  const updateCommentDraft = (reportCardId: number, field: "teacher" | "headteacher", value: string) => {
    setCommentDraftsById((current) => ({
      ...current,
      [reportCardId]: {
        ...(current[reportCardId] || { teacher: "", headteacher: "" }),
        [field]: value
      }
    }));
  };

  const hasDraftChanges = (reportCard: ReportCard) => {
    const drafts = getCommentDraftsForCard(reportCard);
    return drafts.teacher !== (reportCard.classTeacherComment || "")
      || drafts.headteacher !== (reportCard.headteacherComment || "");
  };

  const applySavedCommentsToState = (reportCard: ReportCard, teacherComment: string, headteacherComment: string) => {
    const normalizedTeacherComment = teacherComment || null;
    const normalizedHeadteacherComment = headteacherComment || null;

    setReportCardDetailsById((current) => ({
      ...current,
      [reportCard.id]: {
        ...(current[reportCard.id] || reportCard),
        classTeacherComment: normalizedTeacherComment,
        headteacherComment: normalizedHeadteacherComment
      }
    }));

    setReportCards((current) =>
      current.map((card) =>
        card.id === reportCard.id
          ? {
              ...card,
              classTeacherComment: normalizedTeacherComment,
              headteacherComment: normalizedHeadteacherComment
            }
          : card
      )
    );

    setSelectedReportCard((current) =>
      current?.id === reportCard.id
        ? {
            ...current,
            classTeacherComment: normalizedTeacherComment,
            headteacherComment: normalizedHeadteacherComment
          }
        : current
    );
  };

  const handlePrint = () => {
    const printableCards = document.querySelectorAll(".report-card-results .report-card-print-wrap");
    if (printableCards.length === 0) {
      setMessage("No report cards available to print.");
      return;
    }

    const printClass = "report-card-printing";
    const cleanupPrintMode = () => {
      document.body.classList.remove(printClass);
      window.removeEventListener("afterprint", cleanupPrintMode);
    };

    document.body.classList.add(printClass);
    window.addEventListener("afterprint", cleanupPrintMode, { once: true });
    window.setTimeout(() => {
      window.print();
      window.setTimeout(cleanupPrintMode, 1000);
    }, 150);
  };

  const exportVisibleCardsToPdf = async (cards: ReportCard[]) => {
    const printableCards = Array.from(document.querySelectorAll(".report-card-results .report-card-print-wrap"))
      .filter((node) => node instanceof HTMLElement) as HTMLElement[];
    const pdfMarginMm = 6;
    const pdfInnerWidthMm = 210 - pdfMarginMm * 2;

    if (printableCards.length === 0 || cards.filter((card) => !card.isPlaceholder).length === 0) {
      setMessage("No report cards available to download.");
      return;
    }

    const exportHost = document.createElement("div");
    exportHost.className = "report-card-export-host";
    exportHost.style.background = "#ffffff";
    exportHost.style.padding = "0";
    exportHost.style.margin = "0 auto";
    exportHost.style.width = `${pdfInnerWidthMm}mm`;
    exportHost.style.maxWidth = `${pdfInnerWidthMm}mm`;

    printableCards.forEach((cardNode, index) => {
      const clone = cardNode.cloneNode(true) as HTMLElement;
      clone.style.width = `${pdfInnerWidthMm}mm`;
      clone.style.maxWidth = `${pdfInnerWidthMm}mm`;
      clone.style.margin = "0 auto";
      clone.style.padding = "0";
      clone.style.breakAfter = index === printableCards.length - 1 ? "auto" : "page";
      clone.style.pageBreakAfter = index === printableCards.length - 1 ? "auto" : "always";

      const reportCardView = clone.querySelector(".report-card-view") as HTMLElement | null;
      if (reportCardView) {
        reportCardView.style.width = `${pdfInnerWidthMm}mm`;
        reportCardView.style.maxWidth = `${pdfInnerWidthMm}mm`;
        reportCardView.style.minHeight = "auto";
        reportCardView.style.height = "auto";
        reportCardView.style.margin = "0 auto";
        reportCardView.style.boxSizing = "border-box";
      }

      exportHost.appendChild(clone);
    });

    document.body.appendChild(exportHost);

    const filenameBase = cards.length === 1
      ? `ReportCard_${cards[0].studentName.replace(/\s+/g, "_")}_${cards[0].term}_${cards[0].academicYear}`
      : `ReportCards_${selectedClass || "filtered"}_${selectedTerm || "all"}_${selectedYear || "all"}`.replace(/\s+/g, "_");

    try {
      const html2pdfFactory = ((html2pdf as any)?.default ?? html2pdf) as any;
      const exporter = typeof html2pdfFactory === "function" ? html2pdfFactory() : html2pdfFactory;

      if (!exporter || typeof exporter.set !== "function") {
        throw new Error("Report card PDF export is unavailable.");
      }

      await exporter
        .set({
          margin: [pdfMarginMm, pdfMarginMm, pdfMarginMm, pdfMarginMm],
          filename: `${filenameBase}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] }
        } as any)
        .from(exportHost)
        .save();
    } catch (error: any) {
      setMessage(error?.message || "Failed to download the report cards.");
    } finally {
      exportHost.remove();
    }
  };

  const handleDownloadAction = () => {
    const downloadableCards = reportCards.filter((card) => !card.isPlaceholder);
    if (downloadableCards.length === 0) {
      setMessage("No report cards available to download.");
      return;
    }

    exportVisibleCardsToPdf(downloadableCards);
  };

  const handleSaveComments = async (reportCard: ReportCard) => {
    setActionLoading(true);
    try {
      const drafts = getCommentDraftsForCard(reportCard);
      const actions: Promise<any>[] = [];
      if (canEditTeacherComment) {
        actions.push(
          axios.patch(
            getApiUrl(`report-cards/${reportCard.id}/teacher-comment`),
            { comment: drafts.teacher },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        );
      }
      if (canEditHeadteacherComment) {
        actions.push(
          axios.patch(
            getApiUrl(`report-cards/${reportCard.id}/headteacher-comment`),
            { comment: drafts.headteacher },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        );
      }
      await Promise.all(actions);
      applySavedCommentsToState(reportCard, drafts.teacher, drafts.headteacher);
      setMessage("Comments updated successfully.");
      fetchReportCardDetails(reportCard.id);
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Failed to update comments.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveEditedCommentsSoFar = async (cardsToSave?: ReportCard[]) => {
    const sourceCards = cardsToSave || reportCards.filter((card) => !card.isPlaceholder);
    const dirtyCards = sourceCards.filter((card) => hasDraftChanges(card));

    if (dirtyCards.length === 0) {
      setMessage("No edited comments to save yet.");
      return;
    }

    setActionLoading(true);
    try {
      await Promise.all(
        dirtyCards.map(async (reportCard) => {
          const drafts = getCommentDraftsForCard(reportCard);
          const actions: Promise<any>[] = [];

          if (canEditTeacherComment) {
            actions.push(
              axios.patch(
                getApiUrl(`report-cards/${reportCard.id}/teacher-comment`),
                { comment: drafts.teacher },
                { headers: { Authorization: `Bearer ${token}` } }
              )
            );
          }

          if (canEditHeadteacherComment) {
            actions.push(
              axios.patch(
                getApiUrl(`report-cards/${reportCard.id}/headteacher-comment`),
                { comment: drafts.headteacher },
                { headers: { Authorization: `Bearer ${token}` } }
              )
            );
          }

          await Promise.all(actions);
          applySavedCommentsToState(reportCard, drafts.teacher, drafts.headteacher);
        })
      );

      setMessage(`Saved comments for ${dirtyCards.length} report card${dirtyCards.length === 1 ? "" : "s"}.`);
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Failed to save edited comments.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateBulk = async () => {
    if (!canGenerate) {
      setMessage("You are not allowed to generate report cards.");
      return;
    }
    setActionLoading(true);
    try {
      const endpoint = userRole === "headteacher" ? "report-cards/generate/bulk" : "report-cards/generate/bulk";
      const response = await axios.post(
        getApiUrl(endpoint),
        {
          classId: selectedClass,
          academicYear: selectedYear,
          term: selectedTerm
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(response.data?.message || "Report cards generated successfully.");
      fetchReportCards();
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Failed to generate report cards.");
    } finally {
      setActionLoading(false);
    }
  };

  const getGradeDisplay = (grade: string | null) => {
    return grade || "\u2013";
  };

  const getSubjectLabel = (row: { subjectName: string; subjectShortName?: string | null; subjectLabel?: string | null }) => {
    return row.subjectShortName?.trim() || row.subjectLabel?.trim() || row.subjectName;
  };

  const getScoreDisplay = (score: number | null, decimals = 0) => {
    if (score === null || score === undefined) return "\u2013";
    if (Number.isNaN(score)) return "\u2013";
    return decimals > 0 ? score.toFixed(decimals) : `${Math.round(score)}`;
  };

  const getAwardDisplay = (score: number | null) => {
    if (score === null || score === undefined) return "\u2013";
    if (score >= 80) return "GREEN";
    if (score >= 70) return "YELLOW";
    return "RED";
  };

  const getAverageScore = (row: {
    monthly: number | null;
    midterm: number | null;
    endterm: number | null;
    average: number | null;
  }) => {
    const scores = [row.monthly, row.midterm, row.endterm].filter(
      (value): value is number => typeof value === "number" && !Number.isNaN(value)
    );

    if (scores.length === 0) {
      return row.average ?? null;
    }

    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
  };

  const getCombinedAward = (row: {
    monthly: number | null;
    midterm: number | null;
    endterm: number | null;
    average: number | null;
  }) => {
    const scores = [row.monthly, row.midterm, row.endterm].filter(
      (value): value is number => typeof value === "number" && !Number.isNaN(value)
    );

    if (scores.length === 0) {
      return getAwardDisplay(row.average ?? null);
    }

    const normalizedScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    return getAwardDisplay(normalizedScore);
  };

  const renderReportCardView = (card: ReportCard, isEditing: boolean) => {
    const commentDrafts = getCommentDraftsForCard(card);
    const breakdown = card.assessmentBreakdown || [];
    const hasBreakdown = breakdown.length > 0;
    const fallbackRows = !hasBreakdown && card.details && card.details.length > 0
      ? card.details.map((detail) => ({
          subjectName: detail.subjectName,
          subjectShortName: detail.subjectShortName || null,
          monthly: null,
          midterm: null,
          endterm: null,
          average: detail.score,
          grade: detail.grade
        }))
      : [];
    const rows = hasBreakdown ? breakdown : fallbackRows;
    const getColumnAverage = (values: Array<number | null | undefined>) => {
      const numericValues = values.filter(
        (value): value is number => typeof value === "number" && !Number.isNaN(value)
      );

      if (numericValues.length === 0) {
        return null;
      }

      return numericValues.reduce((sum, value) => sum + value, 0) / numericValues.length;
    };

    const totals = {
      monthly: getColumnAverage(rows.map((row) => row.monthly)),
      midterm: getColumnAverage(rows.map((row) => row.midterm)),
      endterm: getColumnAverage(rows.map((row) => row.endterm))
    };
    const testAverageOverall = getColumnAverage([totals.monthly, totals.midterm, totals.endterm]);
    const averageRows = rows
      .map((row) => getAverageScore(row))
      .filter((value): value is number => value !== null && value !== undefined);
    const totalAverageScore = averageRows.length
      ? averageRows.reduce((acc, value) => acc + value, 0) / averageRows.length
      : null;
    const schoolPhone = schoolDetails.phone || "";
    const schoolAddress = schoolDetails.address || "";
    const schoolEmail = schoolDetails.email || "";
    return (
      <div
        className="report-card-view"
        style={{
          ...(brandPrimary ? { ['--report-accent' as any]: brandPrimary } : {}),
          ...(brandSecondary ? { ['--report-accent-2' as any]: brandSecondary } : {})
        }}
      >
        <div className="report-card-header">
          <div className="report-logo-wrap">
            {schoolLogo ? (
              <img
                src={schoolLogo}
                alt={`${schoolName} logo`}
                className="report-logo"
              />
            ) : (
              <div className="report-logo report-logo-fallback">
                {schoolName.slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="school-info">
            <h1 className="school-name">{schoolName}</h1>
            {(schoolPhone || schoolEmail) && (
              <p className="school-phone">
                {[schoolPhone ? `Tel: ${schoolPhone}` : "", schoolEmail ? `Email: ${schoolEmail}` : ""].filter(Boolean).join(" | ")}
              </p>
            )}
            {schoolAddress && <p className="school-phone">{schoolAddress}</p>}
            <p className="report-title">{String(card.term || "School").toUpperCase()} {card.academicYear} REPORT FORM</p>
          </div>
        </div>

        <div className="student-info">
          <div className="student-info-grid">
            <div>
              <p><strong>Name:</strong> {card.studentName}</p>
              <p><strong>Admission No:</strong> {card.admissionNumber || card.studentId}</p>
            </div>
            <div>
              <p><strong>Grade:</strong> {card.className}</p>
              <p><strong>Age:</strong> {formatAgeDisplay(card.age)}</p>
            </div>
          </div>
        </div>

        <div className="subjects-section">
          <table className="subjects-table">
            <thead>
              <tr>
                <th>S.N</th>
                <th>Subject</th>
                <th>Monthly Test</th>
                <th>Mid-Term</th>
                <th>End-Term</th>
                <th>Average</th>
                <th>Award</th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                <>
                  {rows.map((row, idx) => (
                    <tr key={`${row.subjectName}-${idx}`}>
                      <td>{idx + 1}</td>
                      <td title={row.subjectName}>{getSubjectLabel(row)}</td>
                      <td>{getScoreDisplay(row.monthly)}</td>
                      <td>{getScoreDisplay(row.midterm)}</td>
                      <td>{getScoreDisplay(row.endterm)}</td>
                      <td>{getScoreDisplay(getAverageScore(row))}</td>
                      <td>{getCombinedAward(row)}</td>
                    </tr>
                  ))}
                  <tr className="totals-row">
                    <td colSpan={2}>Test Average</td>
                    <td>{getScoreDisplay(totals.monthly)}</td>
                    <td>{getScoreDisplay(totals.midterm)}</td>
                    <td>{getScoreDisplay(totals.endterm)}</td>
                    <td>{getScoreDisplay(totalAverageScore)}</td>
                    <td>{getAwardDisplay(testAverageOverall)}</td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan={7} className="text-center text-slate-400 py-6">
                    No subject results recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="grading-table">
          <table>
            <thead>
              <tr>
                <th>Range (%)</th>
                <th>Grade</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>90-100</td>
                <td>A+</td>
                <td>Excellent</td>
              </tr>
              <tr>
                <td>80-89</td>
                <td>A</td>
                <td>Very Good</td>
              </tr>
              <tr>
                <td>70-79</td>
                <td>B+</td>
                <td>Good</td>
              </tr>
              <tr>
                <td>60-69</td>
                <td>B</td>
                <td>Satisfactory</td>
              </tr>
              <tr>
                <td>0-59</td>
                <td>C</td>
                <td>Below Average</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="comments-section">
          <div className="comment-block">
            <p className="comment-title">Class Teacher Remarks:</p>
            {isEditing && canEditTeacherComment ? (
              <textarea
                className="comment-input"
                value={commentDrafts.teacher}
                onChange={(e) => updateCommentDraft(card.id, "teacher", e.target.value)}
              />
            ) : (
              <p className="comment-text">{card.classTeacherComment || "\u2013"}</p>
            )}
          </div>
          <div className="comment-block">
            <p className="comment-title">Head Teacher Remarks:</p>
            {isEditing && canEditHeadteacherComment ? (
              <textarea
                className="comment-input"
                value={commentDrafts.headteacher}
                onChange={(e) => updateCommentDraft(card.id, "headteacher", e.target.value)}
              />
            ) : (
              <p className="comment-text">{card.headteacherComment || "\u2013"}</p>
            )}
          </div>
        </div>

      </div>
    );


  };
  const availableStudents = React.useMemo(() => {
    const options = new Map<string, StudentOption>();

    reportCards.forEach((card) => {
      if (!options.has(String(card.studentId))) {
        options.set(String(card.studentId), { id: card.studentId, name: card.studentName });
      }
    });

    students.forEach((student) => {
      if (!options.has(String(student.id))) {
        options.set(String(student.id), student);
      }
    });

    return Array.from(options.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [reportCards, students]);

  useEffect(() => {
    if (!selectedStudent) return;
    const stillExists = availableStudents.some((student) => String(student.id) === selectedStudent);
    if (!stillExists) {
      setSelectedStudent("");
    }
  }, [availableStudents, selectedStudent]);

  return (
    <div className="dashboard-container report-card-page">
      <main className="dashboard-main">
        <div className="utopia-page-container">
          <div className="utopia-table-header report-card-controls">
            <div>
              <h2>Report Cards</h2>
              <p className="page-subtitle">View and print student report cards.</p>
            </div>
            <div className="flex items-center gap-2">
              {(canEditTeacherComment || canEditHeadteacherComment) && (
                <button
                  className={editingAllComments ? "utopia-btn-primary" : "utopia-btn-secondary"}
                  onClick={() => {
                    if (editingAllComments) {
                      cancelEditingComments();
                      return;
                    }
                    startEditingComments();
                  }}
                  disabled={reportCards.filter((card) => !card.isPlaceholder).length === 0 || actionLoading}
                >
                  {editingAllComments ? "Stop Editing Comments" : "Edit Comments"}
                </button>
              )}
              {canGenerate && (
                <button
                  className="utopia-btn-secondary"
                  onClick={handleDownloadAction}
                >
                  {selectedStudent ? "Download Report" : "Download PDF"}
                </button>
              )}
              <button
                className="utopia-btn-secondary"
                onClick={handlePrint}
              >
                Print
              </button>
            </div>
          </div>

          {message && (
            <div className={`p-4 mb-4 rounded-xl ${message.includes("Error") ? "bg-red-50 text-red-700" : "bg-teal-50 text-teal-700"} text-sm`}>
              {message}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6 p-4 bg-slate-50 rounded-xl report-card-filters">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Class</label>
                <select
                  className="utopia-input"
                  value={selectedClass}
                  onChange={(e) => handleClassChange(e.target.value)}
                >
                  <option value="">All Classes</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={String(cls.id)}>{cls.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Student</label>
                <select
                  className="utopia-input"
                  value={selectedStudent}
                  onChange={(e) => setSelectedStudent(e.target.value)}
                >
                  <option value="">All Students</option>
                  {availableStudents.map((student) => (
                    <option key={student.id} value={String(student.id)}>{student.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Term</label>
                <select
                  className="utopia-input"
                  value={selectedTerm}
                  onChange={(e) => setSelectedTerm(e.target.value)}
                >
                  <option value="">All Terms</option>
                  {terms.map((term) => (
                    <option key={term} value={term}>{term}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Year</label>
                <select
                  className="utopia-input"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  <option value="">All Years</option>
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Search</label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    className="utopia-input pl-10"
                    type="text"
                    placeholder="Student name or ID"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  className="utopia-btn-secondary w-full"
                  onClick={fetchReportCards}
                  disabled={loading}
                >
                  {loading ? "Refreshing..." : "Apply Filters"}
                </button>
              </div>
              {canGenerate && (
                <div className="flex items-end">
                  <button
                    className="utopia-btn-primary w-full"
                    onClick={handleGenerateBulk}
                    disabled={actionLoading || !selectedClass || !selectedTerm || !selectedYear}
                  >
                    {actionLoading ? "Generating..." : "Generate Report Cards"}
                  </button>
                </div>
              )}
            </div>

          {loading ? (
            <div className="p-12 text-center text-slate-500">
              <Loader2 className="animate-spin mx-auto mb-4" size={32} />
              Loading report cards...
            </div>
          ) : (
            <div className="report-cards-grid report-card-results">
            {filtersApplied && reportCards.filter((card) => !card.isPlaceholder).length === 0 && !loading && (
              <div className="no-data">
                <AlertCircle size={48} className="text-slate-400 mb-4" />
                <p>
                  {resultAvailability.rows > 0
                    ? "Assessment results exist, but report cards have not been generated for the selected filters."
                    : "No report cards found for the selected filters."}
                </p>
                {resultAvailability.rows > 0 && (
                  <p className="mt-2 text-sm text-slate-500">
                    Found results for {resultAvailability.learners} learner{resultAvailability.learners === 1 ? "" : "s"} across {resultAvailability.rows} recorded score{resultAvailability.rows === 1 ? "" : "s"}.
                  </p>
                )}
                {canGenerate && resultAvailability.rows > 0 && (
                  <button
                    className="utopia-btn-primary mt-4"
                    onClick={handleGenerateBulk}
                    disabled={actionLoading || !selectedClass || !selectedTerm || !selectedYear}
                  >
                    {actionLoading ? "Generating..." : "Generate Report Cards Now"}
                  </button>
                )}
              </div>
            )}
            {reportCards
              .filter((card) => !card.isPlaceholder)
              .map((card: ReportCard, index: number, visibleCards: ReportCard[]) => {
                const enrichedCard =
                  selectedReportCard?.id === card.id
                    ? selectedReportCard
                    : reportCardDetailsById[card.id] || card;
                const isEditing = editingAllComments;
                return (
                  <React.Fragment key={card.id}>
                    <div className="report-card-print-wrap">
                      {renderReportCardView(enrichedCard, isEditing)}
                    </div>
                    {editingAllComments && (index + 1) % SAVE_PROGRESS_INTERVAL === 0 && index !== visibleCards.length - 1 && (
                      <div className="report-card-save-progress">
                        <button
                          className="utopia-btn-secondary"
                          onClick={() => handleSaveEditedCommentsSoFar(visibleCards.slice(0, index + 1))}
                          disabled={actionLoading}
                        >
                          {actionLoading ? "Saving..." : `Save Edited Comments So Far (${index + 1} Cards)`}
                        </button>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            {editingAllComments && reportCards.filter((card) => !card.isPlaceholder).length > 0 && (
              <div className="report-card-save-progress report-card-save-progress-final">
                <button
                  className="utopia-btn-primary"
                  onClick={() => handleSaveEditedCommentsSoFar()}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Saving..." : "Save Edited Comments So Far"}
                </button>
              </div>
            )}
          </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ReportCardsViewer;
